import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, UserPlus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const user = await register(form);
      toast.success(`Welcome to CTP Kenya, ${user.name.split(" ")[0]}!`);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#059669] text-white mb-4">
            <UserPlus className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Create account</h1>
          <p className="text-[#525252] mt-2">Track orders, save details, reorder fast.</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white border border-[#0A0A0A] p-6 space-y-4">
          <GoogleButton label="Sign up with Google" testId="google-signup-btn" />
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span>or with email</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
          <Field label="Full name"><input data-testid="signup-name" required value={form.name} onChange={u("name")} className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none" /></Field>
          <Field label="Email"><input data-testid="signup-email" required type="email" value={form.email} onChange={u("email")} className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none" /></Field>
          <Field label="M-Pesa Phone"><input data-testid="signup-phone" required value={form.phone} onChange={u("phone")} placeholder="07XXXXXXXX" className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono" /></Field>
          <Field label="Password (min 6 chars)">
            <div className="relative">
              <input
                data-testid="signup-password"
                required
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={u("password")}
                className="w-full px-4 py-3 pr-12 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="signup-password-toggle"
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-[#525252] hover:text-[#0A0A0A]"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          {error && <div className="text-sm text-[#DC2626] border border-[#DC2626] bg-[#DC2626]/5 px-3 py-2">{error}</div>}
          <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full bg-[#0A0A0A] text-white px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#059669] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Create account
          </button>
          <div className="text-center text-sm text-[#525252]">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-[#0A0A0A] hover:text-[#059669]">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">{label}</label>
      {children}
    </div>
  );
}
